import React from 'react'
import { Modal } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import type { FinancialEntry } from '@/lib/services/billing'

type RepeatMode = 'once'
const REPEAT_MODES: Array<{ value: RepeatMode; label: string; desc: string }> = [
  { value: 'once', label: 'Clonar', desc: 'Cópia e cola' },
]

interface Occurrence { due_date: string; description?: string }

interface Props {
  isOpen: boolean
  sourceEntry: FinancialEntry | null
  replicateDate: string
  setReplicateDate: (v: string) => void
  replicateOccurrences: Occurrence[]
  replicateSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
  repeatMode: RepeatMode
  setRepeatMode: (m: RepeatMode) => void
  isMobile?: boolean
}

export default function ReplicateModal({ isOpen, sourceEntry, replicateDate, setReplicateDate, replicateOccurrences, replicateSubmitting, onClose, onConfirm, repeatMode, setRepeatMode, isMobile = false }: Props) {
  const { t, locale } = useTranslation()
  const te = (k: string, p?: Record<string, string>) => t(`billing.entries.${k}`, p)

  if (!sourceEntry) return null

  const previewItems = replicateOccurrences.slice(0, 5)
  const remaining = replicateOccurrences.length - previewItems.length
  const isValid = replicateOccurrences.length > 0 && !!replicateDate

  const LOCALE_CURRENCY: Record<string, string> = { 'pt-BR': 'BRL', 'en-US': 'USD', 'es-ES': 'EUR' }
  const currency = LOCALE_CURRENCY[locale] ?? 'BRL'
  const formatCurrency = (amount: number) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  const formatDate = (dateStr: string) => {
    try {
      const [y, m, d] = dateStr.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString(locale)
    } catch { return dateStr }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !replicateSubmitting && onClose()}
      title={te('replicate.modal.title')}
      size="md"
      mobileTopAligned={false}
      headerLeft={
        isMobile ? (
          <button
            onClick={() => !replicateSubmitting && onClose()}
            disabled={replicateSubmitting}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel') || 'Cancelar'}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={onConfirm}
            disabled={replicateSubmitting || !isValid}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label="Confirmar"
          >
            {replicateSubmitting ? (
              <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={() => !replicateSubmitting && onClose()}
            disabled={replicateSubmitting}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label={t('actions.cancel') || 'Fechar'}
          >
            {!isMobile && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      }
    >
      {sourceEntry && <div className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{sourceEntry.description}</p>

        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 px-4 py-2.5 flex items-center gap-4">
          <div>
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{te('replicate.modal.info.amount')}</span>
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">{formatCurrency(sourceEntry.amount)}</p>
          </div>
          {sourceEntry.party_name_snapshot && (
            <>
              <div className="w-px h-8 bg-blue-200 dark:bg-blue-700" />
              <div>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{te('replicate.modal.info.contact')}</span>
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">{sourceEntry.party_name_snapshot}</p>
              </div>
            </>
          )}
        </div>

        <div className="hidden">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{te('replicate.modal.how')}</label>
          <select
            value={repeatMode}
            onChange={(e) => setRepeatMode(e.target.value as RepeatMode)}
            disabled={true}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            {REPEAT_MODES.map(mode => (
              <option key={mode.value} value={mode.value}>
                {te(`replicate.modal.modes.${mode.value}.label`)} — {te(`replicate.modal.modes.${mode.value}.desc`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {repeatMode === 'once' ? te('replicate.modal.firstDateOnce') : te('replicate.modal.firstDateMultiple')}
          </label>
          <input
            type="date"
            lang={locale}
            value={replicateDate}
            onChange={(e) => setReplicateDate(e.target.value)}
            disabled={replicateSubmitting}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {previewItems.length > 0 && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 p-3 space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {te('replicate.modal.preview', { count: String(replicateOccurrences.length) })}
            </p>
            {previewItems.map((occ, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-slate-700 dark:text-slate-300 font-medium tabular-nums whitespace-nowrap">{formatDate(occ.due_date)}</span>
                <span className="text-slate-500 dark:text-slate-400 text-xs truncate">{occ.description}</span>
              </div>
            ))}
            {remaining > 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500 pt-0.5">{te('replicate.modal.previewMore', { count: String(remaining) })}</p>
            )}
          </div>
        )}

      </div>}
    </Modal>
  )
}
